import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ArrowDown, ArrowUp, Plus, Trash2 } from "lucide-react";

import { Badge, Table, Td, Tr } from "../components/desk.tsx";
import { Button, Empty, ErrorBlock, Input, Price, Spinner } from "../components/ui.tsx";
import { cn } from "@/lib/utils";
import { useSpaces } from "../lib/site-config.ts";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { api } from "../api/client.ts";

/**
 * ═══════════════════════════════════════════════════════════════
 *  后台 · 品类 / 品牌 / 运费模板
 * ═══════════════════════════════════════════════════════════════
 *
 * 🔴 **这三样前台天天在用，后台以前一个界面都没有。**
 *    - 品类是整个前台浏览的骨架：首页的品类直达、分类页、商品筛选、
 *      店铺的**自动货架**（绑了平台品类的那种）全按它走。
 *    - 品牌挂在每一件商品上。
 *    - 运费模板决定每一单收多少运费 —— 建材是大件，运费经常比商品还贵。
 *
 *    以前运营想开一个新品类、改个名字、调一下首重续重，只能连数据库改。
 *
 * ⚠️ 拆成单独一个文件：admin.tsx 已经两千多行，再往里堆就没人敢动了。
 */

interface CategoryRow {
  id: number;
  slug: string;
  name: string;
  parentId: number | null;
  spaces: string[];
  sortOrder: number;
  active: boolean;
}

interface BrandRow {
  id: number;
  slug: string;
  name: string;
  description: string;
  sortOrder: number;
  active: boolean;
}

interface FreightRow {
  id: number;
  orgId: number | null;
  name: string;
  mode: "weight" | "volume" | "piece";
  config: {
    firstQuota?: number;
    firstFeeCents?: number;
    stepQuota?: number;
    stepFeeCents?: number;
    freeThresholdCents?: number;
  };
}

/*
  ⚠️ 空间词表统一从后台配置取（`useSpaces`）—— 以前这里写死五个，
     在「首页空间」里新加一格之后这儿是打不上那个标签的，
     于是首页多出一格永远是空的。
*/
// ═══════════════════════════ 品类 ═══════════════════════════

export function CategoriesTab() {
  const qc = useQueryClient();
  const spaces = useSpaces();
  const q = useQuery({
    queryKey: ["admin", "categories"],
    queryFn: () =>
      api.get<{ items: CategoryRow[]; counts: Record<string, number> }>("/admin/categories"),
  });

  /**
   * 🔴 **后台改完，前台那份缓存也要作废。**
   *    前台的品类走 `keys.categories`，跟后台是两个查询 —— 只作废后台那份的话，
   *    他刚加完一个品类、点去商城一看还是老样子，会以为没保存上。
   */
  const invalidate = () => {
    void qc.invalidateQueries({ queryKey: ["admin", "categories"] });
    void qc.invalidateQueries({ queryKey: ["categories"] });
    void qc.invalidateQueries({ queryKey: ["products"] });
  };

  const [adding, setAdding] = useState(false);
  const [draft, setDraft] = useState({ name: "", parentId: "" as string, spaces: [] as string[] });
  const [editing, setEditing] = useState<CategoryRow | null>(null);
  const [confirmDel, setConfirmDel] = useState<number | null>(null);

  const create = useMutation({
    mutationFn: () =>
      api.post("/admin/categories", {
        name: draft.name.trim(),
        parentId: draft.parentId ? Number(draft.parentId) : null,
        spaces: draft.spaces,
      }),
    onSuccess: () => {
      setDraft({ name: "", parentId: "", spaces: [] });
      setAdding(false);
      invalidate();
    },
  });

  const patch = useMutation({
    mutationFn: (v: { id: number; body: Record<string, unknown> }) =>
      api.patch(`/admin/categories/${v.id}`, v.body),
    onSuccess: () => {
      setEditing(null);
      invalidate();
    },
  });

  const remove = useMutation({
    mutationFn: (id: number) => api.del(`/admin/categories/${id}`),
    onSuccess: () => {
      setConfirmDel(null);
      invalidate();
    },
  });

  /** ⚠️ 本地先动再发请求：等服务端回来才刷新的话，点了要过一会儿才动，看着像没点上。 */
  const move = useMutation({
    mutationFn: (ids: number[]) => api.post("/admin/categories/reorder", { ids }),
    onMutate: async (ids) => {
      const key = ["admin", "categories"];
      await qc.cancelQueries({ queryKey: key });
      const prev = qc.getQueryData<{ items: CategoryRow[]; counts: Record<string, number> }>(key);
      if (prev) {
        const byId = new Map(prev.items.map((c) => [c.id, c]));
        qc.setQueryData(key, {
          ...prev,
          items: ids.map((id) => byId.get(id)).filter(Boolean) as CategoryRow[],
        });
      }
      return { prev, key };
    },
    onError: (_e, _v, ctx) => {
      if (ctx?.prev) qc.setQueryData(ctx.key, ctx.prev);
    },
    onSettled: () => invalidate(),
  });

  if (q.isPending) return <Spinner compact />;
  if (q.error) return <ErrorBlock error={q.error} />;

  const items = q.data!.items;
  const counts = q.data!.counts;
  const roots = items.filter((c) => !c.parentId);
  const nameOf = (id: number | null) => items.find((c) => c.id === id)?.name ?? "";

  const swap = (i: number, j: number) => {
    if (j < 0 || j >= items.length) return;
    const ids = items.map((c) => c.id);
    [ids[i], ids[j]] = [ids[j]!, ids[i]!];
    move.mutate(ids);
  };

  return (
    <>
      {/* ⚠️ 只留操作按钮 —— 页面说明全删了，见 desk.tsx 上的说明。 */}
      <div className="mb-4 flex justify-end">
        <Button size="sm" onClick={() => setAdding(true)}>
          <Plus className="size-4" />
          新增品类
        </Button>
      </div>

      {/*
        🔴 **新增品类走弹窗，不是在表格上面展开一块。**（老板指定）
           内联展开有两个毛病：① 表格被整块往下推，他刚才在看的那一行跳走了；
           ② 表单和表格挤在同一屏里，填到一半分不清哪些是"我正在填的"、
           哪些是"已经存在的"。弹窗把这两件事分开。
      */}
      <Dialog open={adding} onOpenChange={(v) => !v && setAdding(false)}>
        <DialogContent className="max-w-[560px]">
          <DialogHeader>
            <DialogTitle>新增品类</DialogTitle>
            <DialogDescription>
              品类是整个前台浏览的骨架：首页直达、分类页、商品筛选、
              店铺的自动货架都按它走。
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <Input
              label="品类名"
              placeholder="例如：开关插座"
              value={draft.name}
              onChange={(e) => setDraft({ ...draft, name: e.target.value })}
            />

            <div>
              <span className="mb-1.5 block text-[13px] text-muted-foreground">上级品类</span>
              <select
                className="h-11 w-full rounded-lg border border-border bg-background px-3 text-[15px] outline-none focus:border-brand-500"
                value={draft.parentId}
                onChange={(e) => setDraft({ ...draft, parentId: e.target.value })}
              >
                <option value="">顶级品类</option>
                {roots.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.name}
                  </option>
                ))}
              </select>
            </div>

            <div>
              {/* ⚠️ 空间决定它出现在首页「按空间挑」的哪一格里，不填就不出现。 */}
              <span className="mb-1.5 block text-[13px] text-muted-foreground">
                适用空间（决定它出现在首页「按空间挑」的哪几格里）
              </span>
              <div className="flex flex-wrap gap-2">
                {spaces.map((sp) => {
                  const on = draft.spaces.includes(sp.key);
                  return (
                    <button
                      key={sp.key}
                      type="button"
                      onClick={() =>
                        setDraft({
                          ...draft,
                          spaces: on
                            ? draft.spaces.filter((x) => x !== sp.key)
                            : [...draft.spaces, sp.key],
                        })
                      }
                      className={cn(
                        "rounded-lg border px-3 py-1.5 text-[13px] transition-colors",
                        on
                          ? "border-brand-500 bg-brand-50 text-brand-700"
                          : "border-border text-muted-foreground hover:border-brand-300",
                      )}
                    >
                      {sp.label}
                    </button>
                  );
                })}
              </div>
            </div>
          </div>

          {create.error && <ErrorBlock error={create.error} />}

          <DialogFooter>
            <Button variant="ghost" onClick={() => setAdding(false)}>
              取消
            </Button>
            <Button
              loading={create.isPending}
              disabled={!draft.name.trim()}
              onClick={() => create.mutate()}
            >
              保存
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {items.length === 0 ? (
        <Empty title="还没有品类" hint="前台的分类页、商品筛选都靠它，至少要有一个" compact />
      ) : (
        <Table head={["排序", "品类", "上级", "适用空间", "商品数", "状态", "操作"]}>
          {items.map((c, i) => (
            <Tr key={c.id}>
              <Td className="w-[92px]">
                <span className="flex gap-0.5">
                  <button
                    className="flex size-7 items-center justify-center rounded text-muted-foreground transition-colors hover:bg-secondary hover:text-foreground disabled:opacity-30"
                    disabled={i === 0 || move.isPending}
                    onClick={() => swap(i, i - 1)}
                    aria-label="上移"
                  >
                    <ArrowUp className="size-3.5" />
                  </button>
                  <button
                    className="flex size-7 items-center justify-center rounded text-muted-foreground transition-colors hover:bg-secondary hover:text-foreground disabled:opacity-30"
                    disabled={i === items.length - 1 || move.isPending}
                    onClick={() => swap(i, i + 1)}
                    aria-label="下移"
                  >
                    <ArrowDown className="size-3.5" />
                  </button>
                </span>
              </Td>

              <Td>
                {editing?.id === c.id ? (
                  <Input
                    className="h-9 max-w-[220px]"
                    value={editing.name}
                    onChange={(e) => setEditing({ ...editing, name: e.target.value })}
                  />
                ) : (
                  <>
                    {c.name}
                    <span className="ml-2 font-mono text-[11px] text-muted-foreground">
                      {c.slug}
                    </span>
                  </>
                )}
              </Td>

              <Td className="text-muted-foreground">{nameOf(c.parentId) || "—"}</Td>

              <Td className="text-muted-foreground">
                {c.spaces.length
                  ? c.spaces.map((s) => spaces.find((x) => x.key === s)?.label ?? s).join(" / ")
                  : "—"}
              </Td>

              <Td className="tabular-nums">{counts[c.id] ?? 0}</Td>

              <Td>
                {c.active ? <Badge tone="ok">前台可见</Badge> : <Badge tone="mute">已停用</Badge>}
              </Td>

              <Td>
                {confirmDel === c.id ? (
                  <span className="flex items-center gap-2">
                    <span className="text-[12px] text-destructive">真的删掉？</span>
                    <Button size="sm" variant="danger" loading={remove.isPending} onClick={() => remove.mutate(c.id)}>
                      删除
                    </Button>
                    <Button size="sm" variant="ghost" onClick={() => setConfirmDel(null)}>
                      取消
                    </Button>
                  </span>
                ) : editing?.id === c.id ? (
                  <span className="flex gap-2">
                    <Button
                      size="sm"
                      loading={patch.isPending}
                      onClick={() => patch.mutate({ id: c.id, body: { name: editing.name } })}
                    >
                      保存
                    </Button>
                    <Button size="sm" variant="ghost" onClick={() => setEditing(null)}>
                      取消
                    </Button>
                  </span>
                ) : (
                  <span className="flex items-center gap-3 text-[12px]">
                    <button className="text-brand-600 hover:text-brand-700" onClick={() => setEditing(c)}>
                      改名
                    </button>
                    <button
                      className="text-muted-foreground hover:text-foreground"
                      onClick={() => patch.mutate({ id: c.id, body: { active: !c.active } })}
                    >
                      {c.active ? "停用" : "启用"}
                    </button>
                    <button
                      className="text-muted-foreground hover:text-destructive"
                      onClick={() => setConfirmDel(c.id)}
                      aria-label="删除"
                    >
                      <Trash2 className="size-3.5" />
                    </button>
                  </span>
                )}
              </Td>
            </Tr>
          ))}
        </Table>
      )}
      {(patch.error || remove.error || move.error) && (
        <ErrorBlock error={patch.error ?? remove.error ?? move.error} />
      )}
    </>
  );
}

// ═══════════════════════════ 品牌 ═══════════════════════════

export function BrandsTab() {
  const qc = useQueryClient();
  const q = useQuery({
    queryKey: ["admin", "brands"],
    queryFn: () => api.get<{ items: BrandRow[]; counts: Record<string, number> }>("/admin/brands"),
  });
  const invalidate = () => {
    void qc.invalidateQueries({ queryKey: ["admin", "brands"] });
    void qc.invalidateQueries({ queryKey: ["products"] });
  };

  const [adding, setAdding] = useState(false);
  const [name, setName] = useState("");
  const [desc, setDesc] = useState("");
  const [confirmDel, setConfirmDel] = useState<number | null>(null);

  const create = useMutation({
    mutationFn: () => api.post("/admin/brands", { name: name.trim(), description: desc.trim() }),
    onSuccess: () => {
      setName("");
      setDesc("");
      setAdding(false);
      invalidate();
    },
  });
  const patch = useMutation({
    mutationFn: (v: { id: number; body: Record<string, unknown> }) =>
      api.patch(`/admin/brands/${v.id}`, v.body),
    onSuccess: invalidate,
  });
  const remove = useMutation({
    mutationFn: (id: number) => api.del(`/admin/brands/${id}`),
    onSuccess: () => {
      setConfirmDel(null);
      invalidate();
    },
  });

  if (q.isPending) return <Spinner compact />;
  if (q.error) return <ErrorBlock error={q.error} />;

  const items = q.data!.items;
  const counts = q.data!.counts;

  return (
    <>
      {/* ⚠️ 只留操作按钮 —— 页面说明全删了，见 desk.tsx 上的说明。 */}
      <div className="mb-4 flex justify-end">
        <Button size="sm" onClick={() => setAdding(true)}>
          <Plus className="size-4" />
          新增品牌
        </Button>
      </div>

      {/* ⚠️ 跟品类、运费模板用**同一种**新增形态（弹窗）—— 老板：不要一个容器一个样式。 */}
      <Dialog open={adding} onOpenChange={(v) => !v && setAdding(false)}>
        <DialogContent className="max-w-[560px]">
          <DialogHeader>
            <DialogTitle>新增品牌</DialogTitle>
            <DialogDescription>品牌用来在前台筛选和展示，跟品类是两回事。</DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <Input
              label="品牌名"
              placeholder="例如：马可波罗"
              value={name}
              onChange={(e) => setName(e.target.value)}
            />
            <Input label="简介（选填）" value={desc} onChange={(e) => setDesc(e.target.value)} />
          </div>

          {create.error && <ErrorBlock error={create.error} />}

          <DialogFooter>
            <Button variant="ghost" onClick={() => setAdding(false)}>
              取消
            </Button>
            <Button
              loading={create.isPending}
              disabled={!name.trim()}
              onClick={() => create.mutate()}
            >
              保存
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {items.length === 0 ? (
        <Empty title="还没有品牌" compact />
      ) : (
        <Table head={["品牌", "简介", "商品数", "状态", "操作"]}>
          {items.map((b) => (
            <Tr key={b.id}>
              <Td>
                {b.name}
                <span className="ml-2 font-mono text-[11px] text-muted-foreground">{b.slug}</span>
              </Td>
              <Td className="text-muted-foreground">{b.description || "—"}</Td>
              <Td className="tabular-nums">{counts[b.id] ?? 0}</Td>
              <Td>{b.active ? <Badge tone="ok">前台可见</Badge> : <Badge tone="mute">已停用</Badge>}</Td>
              <Td>
                {confirmDel === b.id ? (
                  <span className="flex items-center gap-2">
                    <span className="text-[12px] text-destructive">真的删掉？</span>
                    <Button size="sm" variant="danger" loading={remove.isPending} onClick={() => remove.mutate(b.id)}>
                      删除
                    </Button>
                    <Button size="sm" variant="ghost" onClick={() => setConfirmDel(null)}>
                      取消
                    </Button>
                  </span>
                ) : (
                  <span className="flex items-center gap-3 text-[12px]">
                    <button
                      className="text-muted-foreground hover:text-foreground"
                      onClick={() => patch.mutate({ id: b.id, body: { active: !b.active } })}
                    >
                      {b.active ? "停用" : "启用"}
                    </button>
                    <button
                      className="text-muted-foreground hover:text-destructive"
                      onClick={() => setConfirmDel(b.id)}
                      aria-label="删除"
                    >
                      <Trash2 className="size-3.5" />
                    </button>
                  </span>
                )}
              </Td>
            </Tr>
          ))}
        </Table>
      )}
      {(patch.error || remove.error) && <ErrorBlock error={patch.error ?? remove.error} />}
    </>
  );
}

// ═══════════════════════════ 运费模板 ═══════════════════════════

const MODE_LABEL: Record<string, string> = { weight: "按重量", volume: "按体积", piece: "按件数" };
const MODE_UNIT: Record<string, string> = { weight: "克", volume: "毫升", piece: "件" };

export function FreightTab() {
  const qc = useQueryClient();
  const q = useQuery({
    queryKey: ["admin", "freight"],
    queryFn: () => api.get<{ items: FreightRow[] }>("/admin/freight-templates"),
  });
  const invalidate = () => qc.invalidateQueries({ queryKey: ["admin", "freight"] });

  const [adding, setAdding] = useState(false);
  const [form, setForm] = useState({
    name: "",
    mode: "weight" as FreightRow["mode"],
    firstQuota: "1000",
    firstFee: "10",
    stepQuota: "1000",
    stepFee: "5",
    freeThreshold: "",
  });
  const [confirmDel, setConfirmDel] = useState<number | null>(null);

  const body = () => ({
    name: form.name.trim(),
    mode: form.mode,
    config: {
      firstQuota: Number(form.firstQuota) || 0,
      firstFeeCents: Math.round(Number(form.firstFee) * 100) || 0,
      stepQuota: Number(form.stepQuota) || 1,
      stepFeeCents: Math.round(Number(form.stepFee) * 100) || 0,
      /** ⚠️ 留空 = 不包邮。填 0 会变成"0 元就包邮"，那是全场包邮。 */
      freeThresholdCents: form.freeThreshold ? Math.round(Number(form.freeThreshold) * 100) : 0,
    },
  });

  const create = useMutation({
    mutationFn: () => api.post("/admin/freight-templates", body()),
    onSuccess: () => {
      setAdding(false);
      invalidate();
    },
  });
  const remove = useMutation({
    mutationFn: (id: number) => api.del(`/admin/freight-templates/${id}`),
    onSuccess: () => {
      setConfirmDel(null);
      invalidate();
    },
  });

  if (q.isPending) return <Spinner compact />;
  if (q.error) return <ErrorBlock error={q.error} />;

  const items = q.data!.items;
  const unit = MODE_UNIT[form.mode] ?? "";

  return (
    <>
      {/* ⚠️ 只留操作按钮 —— 页面说明全删了，见 desk.tsx 上的说明。 */}
      <div className="mb-4 flex justify-end">
        <Button size="sm" onClick={() => setAdding(true)}>
          <Plus className="size-4" />
          新增模板
        </Button>
      </div>

      {/* ⚠️ 跟品类、品牌用同一种新增形态（弹窗）。表单字段多，弹窗放宽到 720。 */}
      <Dialog open={adding} onOpenChange={(v) => !v && setAdding(false)}>
        <DialogContent className="max-w-[720px]">
          <DialogHeader>
            <DialogTitle>新增运费模板</DialogTitle>
            <DialogDescription>
              商品按它算运费。⚠️ 体积模式下额度的单位是毫升，重量是克 —— 两者不能直接比。
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-[minmax(0,1fr)_180px]">
              <Input
                label="模板名"
                placeholder="例如：瓷砖大件（华南）"
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
              />
              <div>
                <span className="mb-1.5 block text-[13px] text-muted-foreground">计费方式</span>
                <select
                  className="h-11 w-full rounded-lg border border-border bg-background px-3 text-[15px] outline-none focus:border-brand-500"
                  value={form.mode}
                  onChange={(e) => setForm({ ...form, mode: e.target.value as FreightRow["mode"] })}
                >
                  {Object.entries(MODE_LABEL).map(([k, v]) => (
                    <option key={k} value={k}>
                      {v}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3 md:grid-cols-4 md:gap-4">
              <Input
                label={`首重额度（${unit}）`}
                inputMode="numeric"
                value={form.firstQuota}
                onChange={(e) => setForm({ ...form, firstQuota: e.target.value })}
              />
              <Input
                label="首重费用（元）"
                inputMode="decimal"
                value={form.firstFee}
                onChange={(e) => setForm({ ...form, firstFee: e.target.value })}
              />
              <Input
                label={`续重步长（${unit}）`}
                inputMode="numeric"
                value={form.stepQuota}
                onChange={(e) => setForm({ ...form, stepQuota: e.target.value })}
              />
              <Input
                label="每步费用（元）"
                inputMode="decimal"
                value={form.stepFee}
                onChange={(e) => setForm({ ...form, stepFee: e.target.value })}
              />
            </div>

            <div className="max-w-[260px]">
              <Input
                label="满多少包邮（元，留空 = 不包邮）"
                inputMode="decimal"
                value={form.freeThreshold}
                onChange={(e) => setForm({ ...form, freeThreshold: e.target.value })}
              />
            </div>
          </div>

          {create.error && <ErrorBlock error={create.error} />}

          <DialogFooter>
            <Button variant="ghost" onClick={() => setAdding(false)}>
              取消
            </Button>
            <Button
              loading={create.isPending}
              disabled={!form.name.trim()}
              onClick={() => create.mutate()}
            >
              保存
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {items.length === 0 ? (
        <Empty
          title="还没有运费模板"
          hint="没有模板时按系统默认规则算运费；建了模板才能自己定首重续重和包邮门槛"
          compact
        />
      ) : (
        <Table head={["模板", "计费方式", "首重", "续重", "包邮门槛", "归属", "操作"]}>
          {items.map((f) => (
            <Tr key={f.id}>
              <Td>{f.name}</Td>
              <Td className="text-muted-foreground">{MODE_LABEL[f.mode] ?? f.mode}</Td>
              <Td className="tabular-nums">
                {f.config.firstQuota ?? 0} {MODE_UNIT[f.mode]} /{" "}
                <Price cents={f.config.firstFeeCents ?? 0} />
              </Td>
              <Td className="tabular-nums">
                每 {f.config.stepQuota ?? 0} {MODE_UNIT[f.mode]} 加{" "}
                <Price cents={f.config.stepFeeCents ?? 0} />
              </Td>
              <Td className="tabular-nums">
                {f.config.freeThresholdCents ? <Price cents={f.config.freeThresholdCents} /> : "不包邮"}
              </Td>
              {/* ⚠️ `orgId = null` 是平台级模板，所有供应商没自己配时都落到它。 */}
              <Td className="text-muted-foreground">{f.orgId ? `供应商 #${f.orgId}` : "平台通用"}</Td>
              <Td>
                {confirmDel === f.id ? (
                  <span className="flex items-center gap-2">
                    <span className="text-[12px] text-destructive">真的删掉？</span>
                    <Button size="sm" variant="danger" loading={remove.isPending} onClick={() => remove.mutate(f.id)}>
                      删除
                    </Button>
                    <Button size="sm" variant="ghost" onClick={() => setConfirmDel(null)}>
                      取消
                    </Button>
                  </span>
                ) : (
                  <button
                    className="text-[12px] text-muted-foreground hover:text-destructive"
                    onClick={() => setConfirmDel(f.id)}
                  >
                    删除
                  </button>
                )}
              </Td>
            </Tr>
          ))}
        </Table>
      )}
      {remove.error && <ErrorBlock error={remove.error} />}
    </>
  );
}
